import type { ApolloCache, Reference, StoreObject } from '@apollo/client';
import { gql } from '@apollo/client';
import { questionTypeToReportColumnDataType } from 'src/components/reports/question-column-data-type';
import type { Apollo } from 'src/gql/generated';

type CreatedExtractionQuestion =
  Apollo.CreateExtractionQuestionMutation['createExtractionQuestion'];
type ExtractionColumnOptionPatch = Partial<
  Pick<Apollo.ExtractionReportColumn, 'dataType' | 'displayName'>
>;

const reportTypename: Apollo.Report['__typename'] = 'Report';
const extractionReportColumnTypename: Apollo.ExtractionReportColumn['__typename'] =
  'ExtractionReportColumn';
const reportColumnOptionsField = 'reportColumnOptions' satisfies keyof Apollo.Query;
const reportColumnOptionsOfReportField = 'columnOptions' satisfies keyof Apollo.Report;
const extractionQuestionsField =
  'extractionQuestions' satisfies keyof Apollo.Query['extractionQuestionsForDocument'];

/**
 * Adds a just-created question to every cached list of the org's questions.
 * `Query.reportColumnOptions` is the one that matters most: the repository
 * grid's grouping picker and the columns panel read it, while the report that
 * created the question only patches its own `Report.columnOptions`. Without
 * this the new column reaches the Documents page only on a full refetch.
 */
export function addCreatedExtractionQuestionToCache(
  cache: ApolloCache,
  question: CreatedExtractionQuestion
): void {
  const questionRef = cache.writeFragment({
    data: question,
    fragment: gql`
      fragment NewExtractionQuestion on ExtractionQuestion {
        __typename
        id
      }
    `,
  });
  if (!questionRef) return;

  // Same shape `getReportColumnOptions` builds on the server: every question column is groupable and a blank title reads "Untitled".
  const columnOption = {
    __typename: extractionReportColumnTypename,
    dataType: questionTypeToReportColumnDataType(question.type),
    displayName: question.title || 'Untitled',
    isGroupable: true,
    question: questionRef,
    questionId: question.id,
  } satisfies Record<keyof Apollo.ExtractionReportColumn, unknown>;

  cache.modify({
    fields: {
      extractionQuestions: (refs: readonly Reference[] = []) => [...refs, questionRef],
      extractionQuestionsForDocument: (existing: StoreObject | Reference, { readField }) => ({
        ...existing,
        [extractionQuestionsField]: [
          ...(readField<Reference[]>(extractionQuestionsField, existing) ?? []),
          questionRef,
        ],
      }),
      [reportColumnOptionsField]: (columnOptions: readonly (StoreObject | Reference)[] = []) => [
        ...columnOptions,
        columnOption,
      ],
    },
  });
}

/**
 * Patches one question's column option wherever the cache keeps a copy: the
 * org-wide `Query.reportColumnOptions`, plus `Report.columnOptions` when the
 * edit happens inside a report. Both carry a denormalized `displayName` and
 * `dataType`, so a rename or type change has to reach them by hand; the
 * normalized question entity updates on its own.
 */
export function patchExtractionColumnOptionInCache(
  cache: ApolloCache,
  options: { patch: ExtractionColumnOptionPatch; questionId: string; reportId: string | null }
): void {
  const { patch, questionId, reportId } = options;
  const reportCacheId = reportId
    ? cache.identify({ __typename: reportTypename, id: reportId })
    : undefined;
  const targets: { field: string; id?: string }[] = [{ field: reportColumnOptionsField }];
  if (reportCacheId) targets.push({ field: reportColumnOptionsOfReportField, id: reportCacheId });

  for (const { field, id } of targets)
    cache.modify({
      fields: {
        [field]: (columnOptions: readonly (StoreObject | Reference)[] = [], { readField }) =>
          columnOptions.map(columnOption =>
            readField('__typename', columnOption) === extractionReportColumnTypename &&
            readField('questionId', columnOption) === questionId
              ? { ...columnOption, ...patch }
              : columnOption
          ),
      },
      // `modify` bails out on a present-but-undefined `id`; the root query is addressed by omitting it.
      ...(id ? { id } : {}),
    });
}
